import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowSquareOut,
  Copy,
  Paperclip,
  Trash,
  UserPlus,
  UsersThree,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, API } from "@/lib/api";

const STATUS_STYLE = {
  New: "border-indigo-400/40 bg-indigo-500/10 text-indigo-200",
  Contacted: "border-sky-400/40 bg-sky-500/10 text-sky-200",
  Quoted: "border-amber-400/40 bg-amber-500/10 text-amber-200",
  Won: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200",
  Lost: "border-zinc-700 text-zinc-500",
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export default function Leads() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], counts: {} });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const [list, m] = await Promise.all([
      api.get("/leads", { params: { q: q || undefined } }),
      api.get("/leads/meta"),
    ]);
    setItems(list.data);
    setMeta(m.data);
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = items.filter(
    (l) => status.length === 0 || status.includes(l.status),
  );

  const publicLink = `${window.location.origin}/leads/new`;
  const token = localStorage.getItem("sw_token");

  const setLead = async (lead, patch) => {
    const { data } = await api.put(`/leads/${lead.id}`, patch);
    setItems((cur) => cur.map((l) => (l.id === lead.id ? data : l)));
    if (patch.status) toast.success(`${data.name} → ${data.status}`);
    load();
  };

  const convert = async (lead) => {
    const { data } = await api.post(`/leads/${lead.id}/convert`);
    toast.success(
      data.already
        ? "Account already exists"
        : `${lead.name} added to accounts`,
    );
    nav(`/customers/${data.customer_id}`);
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete the lead from ${lead.name}?`)) return;
    await api.delete(`/leads/${lead.id}`);
    toast.success("Lead deleted");
    load();
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Leads" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="leads-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Leads
              </h1>
              <p
                className="mt-1 text-sm text-zinc-500"
                data-testid="leads-count"
              >
                {items.length} enquir{items.length === 1 ? "y" : "ies"} ·{" "}
                {meta.counts?.New || 0} new
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
            <Button
              data-testid="leads-copy-link"
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(publicLink);
                toast.success("Public form link copied");
              }}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              <Copy size={14} weight="bold" className="mr-2" /> Copy form link
            </Button>
            <Button
              data-testid="leads-open-form"
              variant="outline"
              onClick={() => window.open("/leads/new", "_blank")}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
            >
              <ArrowSquareOut size={14} weight="bold" className="mr-2" /> Open
              form
            </Button>
          </div>
        </div>

        <FilterBar
          className="mt-6"
          testidPrefix="leads"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search name, phone, address, color…",
            testid: "leads-search",
          }}
          sections={[
            {
              key: "status",
              label: "Status",
              testid: "leads-filters",
              multi: true,
              values: status,
              allValue: "All",
              onChange: setStatus,
              options: ["All", ...(meta.statuses || [])].map((s) => [
                s,
                s !== "All" && meta.counts?.[s] ? `${s} ${meta.counts[s]}` : s,
                `leads-filter-${s.toLowerCase()}`,
              ]),
            },
          ]}
        />

        {shown.length === 0 ? (
          <div
            className="mt-4 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
            data-testid="leads-empty"
          >
            <UsersThree
              size={26}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              No leads yet — share the form link and enquiries land here.
            </p>
          </div>
        ) : (
          <div className="mt-4 space-y-3" data-testid="leads-list">
            {shown.map((l) => (
              <div
                key={l.id}
                data-testid={`lead-card-${l.id}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition-colors duration-150 hover:border-indigo-400/40"
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-base font-bold">{l.name}</h3>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                          STATUS_STYLE[l.status] || "border-zinc-700"
                        }`}
                        data-testid={`lead-status-${l.id}`}
                      >
                        {l.status}
                      </span>
                      <span className="font-mono-data text-[10px] text-zinc-600">
                        {l.code}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      {[l.company, l.phone, l.email]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400">
                      {l.address} · {l.project_type} · {l.timeline}
                      {l.budget ? ` · ${l.budget}` : ""}
                    </p>
                    {l.working_with && (
                      <p
                        className="mt-1 text-xs text-zinc-400"
                        data-testid={`lead-working-with-${l.id}`}
                      >
                        Working with{" "}
                        <span className="text-zinc-200">{l.working_with}</span>
                      </p>
                    )}
                    {(l.colors?.length || l.other_colors) && (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5"
                        data-testid={`lead-colors-${l.id}`}
                      >
                        {(l.colors || []).map((c) => (
                          <span
                            key={c}
                            className="rounded-full border border-indigo-400/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] text-indigo-200"
                          >
                            {c}
                          </span>
                        ))}
                        {l.other_colors && (
                          <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] text-zinc-400">
                            {l.other_colors}
                          </span>
                        )}
                      </div>
                    )}
                    {l.message && (
                      <p className="mt-2 line-clamp-2 text-xs italic text-zinc-500">
                        “{l.message}”
                      </p>
                    )}
                    {(l.files || []).filter((x) => !x.is_deleted).length >
                      0 && (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5"
                        data-testid={`lead-files-${l.id}`}
                      >
                        {(l.files || [])
                          .filter((x) => !x.is_deleted)
                          .map((file) => (
                            <a
                              key={file.id}
                              data-testid={`lead-file-link-${file.id}`}
                              href={`${API}/leads/file/${file.storage_path}?auth=${token}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-1.5 rounded-full border border-zinc-700 px-2.5 py-1 text-[10px] text-zinc-300 transition-colors duration-150 hover:border-indigo-400/50 hover:text-white"
                            >
                              <Paperclip size={11} weight="bold" />
                              {file.name}
                            </a>
                          ))}
                      </div>
                    )}
                    <p className="font-mono-data mt-2 text-[10px] text-zinc-600">
                      {when(l.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Select
                      value={l.status}
                      onValueChange={(v) => setLead(l, { status: v })}
                    >
                      <SelectTrigger
                        data-testid={`lead-set-status-${l.id}`}
                        className="h-10 w-36 border-zinc-800 bg-zinc-950 text-xs"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(meta.statuses || []).map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      data-testid={`lead-convert-${l.id}`}
                      variant="outline"
                      onClick={() => convert(l)}
                      className="h-10 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      <UserPlus size={13} weight="bold" className="mr-1.5" />
                      {l.customer_id ? "Open account" : "Create account"}
                    </Button>
                    <Button
                      data-testid={`lead-notes-${l.id}`}
                      variant="outline"
                      onClick={() => setOpen(open === l.id ? null : l.id)}
                      className="h-10 border-zinc-700 bg-transparent text-[10px] uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Notes
                    </Button>
                    <Button
                      data-testid={`lead-delete-${l.id}`}
                      variant="outline"
                      onClick={() => remove(l)}
                      className="h-10 border-zinc-700 bg-transparent text-[10px] text-red-400 hover:bg-zinc-800"
                    >
                      <Trash size={13} weight="bold" />
                    </Button>
                  </div>
                </div>
                {open === l.id && (
                  <div className="mt-3 border-t border-zinc-800 pt-3">
                    <Textarea
                      data-testid={`lead-notes-input-${l.id}`}
                      defaultValue={l.notes || ""}
                      onBlur={(e) => setLead(l, { notes: e.target.value })}
                      placeholder="Call notes, what they picked, next step…"
                      className="min-h-[90px] border-zinc-800 bg-zinc-950 text-sm"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
